var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { QuadArray } from 'lincd/collections/QuadArray';
import { QuadSet } from 'lincd/collections/QuadSet';
import { defaultGraph, Graph } from 'lincd/models';
import { PropertyShape } from 'lincd/shapes/SHACL';
import { Shape } from 'lincd/shapes/Shape';
import { LinkedStorage } from 'lincd/utils/LinkedStorage';
import { lincdStorage } from '../ontologies/lincd-storage.js';
import { linkedShape } from '../package.js';
import { createLocal, deleteLocal, resolveLocal, } from 'lincd/utils/LocalQueryResolver';
import { updateLocal } from 'lincd/utils/LocalQueryResolver';
let InMemoryStore = class InMemoryStore extends Shape {
    constructor(n) {
        super(n);
    }
    init() {
        // console.log('Init ' + this.toString());
        if (!this.initPromise) {
            this.initPromise = this.onInit().then(() => {
                if (typeof process !== 'undefined' &&
                    process.env.SKIP_STORE_INIT &&
                    process.env.SKIP_STORE_INIT === 'true') {
                    this.contents = new QuadSet();
                }
                else {
                    return this.loadContents();
                }
            });
        }
        return this.initPromise;
    }
    /**
     * This method is called when the store is first initialised before loading contents
     * Overwrite this method to do store specific things to start up the store, besides calling loadContents
     */
    onInit() {
        return Promise.resolve();
    }
    loadContents() {
        //by default an in-memory store starts empty - it has no permanent storage
        //overwrite this method to change that
        this.contents = new QuadSet();
        return Promise.resolve(this.contents);
    }
    /**
     * returns the contents of the InMemoryStore as a QuadSet
     * do NOT modify the returned QuadSet directly. Add or remove contents to this store instead
     */
    getContents() {
        return this.contents;
    }
    update(toAdd, toRemove) {
        return this.init().then(() => {
            // console.log('Updating ' + this.toString() + ':\nAdding ' + (toAdd ? (toAdd.size || toAdd.length) : 0) + ', removing ' + (toRemove ? (toRemove.size || toRemove.length) : 0));
            if (toAdd) {
                this._addMultiple(toAdd);
            }
            if (toRemove) {
                this._deleteFromContents(toRemove);
            }
            //this method should only get called if either toAdd or toRemove is not empty
            if (toAdd || toRemove) {
                return this._onContentsUpdated();
            }
        });
    }
    async createQuery(query) {
        // await JSONWriter.stringify(query).then((str) => {
        //   console.log(str);
        // });
        return Promise.resolve(createLocal(query));
    }
    async updateQuery(query) {
        // await JSONWriter.stringify(query).then((str) => {
        //   console.log(str);
        // });
        return Promise.resolve(updateLocal(query));
    }
    async deleteQuery(query) {
        // await JSONWriter.stringify(query).then((str) => {
        //   console.log(str);
        // });
        return Promise.resolve(deleteLocal(query));
    }
    getDefaultGraph() {
        //NOTE: we changed this from a specific graph for each store BACK null, which means things will be stored in the default graph
        // because storing quads in multiple graphs easily becomes error-prone.
        // Removing a triple from one store might not remove it from the main graph.
        // for example, auth will store users serialised as JSONLD in sessions, these quads get added back to the main graph
        // until we have a better solution for this, we aim to only use the default graph as much as possible (which means no specific graph os used, thus return null)
        // return null;
        // return defaultGraph;
        return Graph.getOrCreate(this.namedNode.uri);
    }
    add(quad) {
        return this.init().then(() => {
            this._addToContents(new QuadArray(quad));
            return this._onContentsUpdated().then(() => {
                return true;
            });
        });
    }
    addMultiple(quads) {
        return this.init().then(() => {
            this._addMultiple(quads);
            return this._onContentsUpdated().then(() => {
                return true;
            });
        });
    }
    delete(quad) {
        return this.init().then(() => {
            // this.contents.delete(quad);
            this._deleteFromContents(new QuadArray(quad));
            return this._onContentsUpdated().then(() => {
                return true;
            });
        });
    }
    deleteMultiple(quads) {
        return this.init().then(() => {
            this._deleteFromContents(quads);
            //only complete the promise after the contents have been updated
            return this._onContentsUpdated().then(() => {
                return true;
            });
        });
    }
    clearProperties(subjectToPredicates) {
        return this.init().then(() => {
            let toDelete = new QuadSet();
            this.contents.forEach((q) => {
                if (subjectToPredicates.has(q.subject) &&
                    subjectToPredicates.get(q.subject).has(q.predicate)) {
                    toDelete.add(q);
                }
            });
            if (toDelete.size > 0) {
                return this.deleteMultiple(toDelete);
            }
            return false;
        });
    }
    setURIs(nodeToCurrentUriMap) {
        //by default an in-memory store does not support setting URIs,
        // if it's used in a local context for in memory storage it doesn't really care about permanent storage URI's
        // if it's used in a different context, this method should be overwritten
        return Promise.resolve([]);
    }
    removeNodes(nodes) {
        //when storage calls removeNodes, all quads have already been removed locally
        //and an in memory store always holds all data in memory,
        //so there is nothing to do here
        return Promise.resolve(true);
    }
    async selectQuery(query) {
        // await JSONWriter.stringify(query).then((str) => {
        //   console.log(str);
        // });
        return Promise.resolve(resolveLocal(query));
    }
    loadShape(shapeInstance, request) {
        return this.init().then(() => {
            return this.getRequestQuads(shapeInstance, request);
            //for testing: add timer
            // return new Promise((resolve, reject) => {
            //   setTimeout(() => {
            //get quads for each (nested) shape / property shape
            // let quads = this.getRequestQuads(shapeInstance, request);
            // return resolve(quads);
            // }, 1500);
            // });
        });
    }
    loadShapes(shapeInstances, request) {
        return this.init().then(() => {
            let quads = new QuadArray();
            shapeInstances.forEach((shapeInstance) => {
                this.getRequestQuads(shapeInstance, request, quads);
            });
            return quads;
            // return new Promise((resolve, reject) => {
            //   setTimeout(() => {
            //     //get quads for each (nested) shape / property shape
            //     //TODO: update getRequestQuads to work with shape set
            //     // possibly even merge definitions of loadShape and loadShapes? is it select? query?
            //     // let quads = this.getRequestQuads(shapeInstances, request);
            //     // return Promise.resolve(quads);
            //     // return resolve(quads);
            //     return resolve([] as any);
            //   }, 1500);
            // });
        });
    }
    onContentsUpdated() {
        //by default in memory store does nothing here. But extending classes could choose to sync to a more permanent form of storage
        return Promise.resolve(false);
    }
    get isWorker() {
        return false;
    }
    async _onContentsUpdated() {
        if (typeof process !== 'undefined' &&
            process.env.SKIP_STORE_INIT &&
            process.env.SKIP_STORE_INIT === 'true') {
            return;
        }
        //TODO: remove this, just handy for testing. In real life the actual store will take core of its multicore setup. Multiple timers is not an issue
        //support for multi core added. Where currently only 1 store will take care of updates
        if (this.isWorker) {
            // this.log(`Not storing, because this is not the primary worker`);
            return;
        }
        //don't store if we are already storing
        if (this.storagePromise) {
            if (!this.storeAgain) {
                // console.log(
                //   this.label + ' - Contents updated: Storing again, later',
                // );
                this.storeAgain = true;
            }
            // console.log(
            //   this.label + ' - Contents updated: Returning existing storage promise',
            // );
            return this.storagePromise;
        }
        //don't store if there is a timeout set
        if (this.storageTimeoutPromise) {
            // console.log(
            //   this.label + ' - Contents updated: Returning TIMEOUT promise',
            // );
            this.storeAgain = true;
            return this.storageTimeoutPromise;
        }
        // console.log(
        //   this.label + ' - Contents updated: Storing NOW',
        // );
        this.storagePromise = this.onContentsUpdated().then((result) => {
            delete this.storagePromise;
            //after storing, wait a bit before allowing this method to run again (opening up IO)
            this._setStorageTimeout();
            //however the promise should be resolved immediately
            return result;
        });
        return this.storagePromise;
    }
    _setStorageTimeout(extraDelay = 0) {
        // console.log(
        //   this.label +
        //     ' (' +
        //     process.pid +
        //     '): Setting timeout of ' +
        //     this.getMinStoreDelay() / 1000 +
        //     ' seconds before potentially storing again.',
        // );
        if (this.storageTimeout) {
            // console.log(
            //   this.label + ' (' + process.pid + `): Resetting previous timeout ${this.storageTimeout}`,
            // );
            clearTimeout(this.storageTimeout);
        }
        if (!this.storageTimeoutPromise) {
            let res, rej;
            let p = new Promise((resolve, reject) => {
                res = resolve;
                rej = reject;
            });
            this.storageTimeoutPromise = { promise: p, resolve: res, reject: rej };
        }
        //reset timeout
        this.storageTimeout = setTimeout(() => {
            // console.log(
            //   this.label +
            //   ' (' +
            //   process.pid +
            //   `): Timeout ${this.storageTimeout} completed.`,
            // );
            const resolve = this.storageTimeoutPromise.resolve;
            delete this.storageTimeout;
            delete this.storageTimeoutPromise;
            if (this.storeAgain) {
                // console.log(
                //   this.label +
                //   ' (' +
                //   process.pid +
                //   '): Timeout completed. Storing again straight away',
                // );
                this.storeAgain = false;
                resolve(this._onContentsUpdated());
            }
        }, this.getMinStoreDelay() + extraDelay);
        // console.log(this.label+' ('+process.pid+`): Timeout ${this.storageTimeout} set for ${this.getMinStoreDelay() / 1000} seconds`);
        return this.storageTimeoutPromise;
    }
    /**
     * Returns the minimum number of miliseconds before onContentsUpdated is called again
     * @protected
     */
    getMinStoreDelay() {
        return 0;
    }
    _addToContents(quads) {
        //get the target graph for this store, if configured
        let graph = LinkedStorage.getGraphForStore(this) || this.targetGraph || defaultGraph;
        //if there is one, move the quads into that graph
        if (graph) {
            quads = quads.moveTo(graph, false);
        }
        this.contents.addFrom(quads);
        return quads;
    }
    _addMultiple(quads) {
        this._addToContents(quads);
    }
    _deleteFromContents(quads) {
        //first we add the quads to the right graph (which effectively ADDS these quads to this store)
        //then we remove them from the contents
        //get the target graph for this store, if configured
        let graph = LinkedStorage.getGraphForStore(this) || this.targetGraph || defaultGraph;
        //if there is one, move the quads into that graph
        if (graph) {
            quads = quads.moveTo(graph, false);
        }
        quads.forEach((quad) => {
            this.contents.delete(quad);
            quad.remove(false);
        });
        return quads;
    }
    setContents(contents) {
        this.contents = contents;
        return this._onContentsUpdated();
    }
    getRequestQuads(source, request, quads = new QuadArray()) {
        // let {shape, properties}: {shape: typeof Shape; properties?: (PropertyShape | BoundPropertyShapes)[]} = request;
        request.forEach((propertyRequest) => {
            let subRequest;
            let propertyShape;
            let propertyShapeSource;
            //if an entry is an array, then it consists of the property shape + a sub request
            if (Array.isArray(propertyRequest)) {
                [propertyShape, subRequest] = propertyRequest;
            }
            else if (propertyRequest instanceof PropertyShape) {
                propertyShape = propertyRequest;
            }
            if (propertyShape) {
                if (source instanceof QuadSet) {
                    propertyShapeSource = source
                        .getObjects()
                        .getQuads(propertyShape.path);
                }
                else if (source instanceof Shape) {
                    propertyShapeSource = source.getQuads(propertyShape.path);
                }
                propertyShapeSource.forEach((q) => quads.push(q));
            }
            if (subRequest) {
                this.getRequestQuads(propertyShapeSource, subRequest, quads);
            }
        });
        return quads;
    }
    unload() {
        this.contents.forEach((q) => {
            q.turnOff();
        });
        this.contents = null;
    }
    async reload() {
        this.unload();
        this.initPromise = null;
        await this.init();
        return Promise.resolve(true);
    }
};
InMemoryStore.targetClass = lincdStorage.InMemoryStore;
InMemoryStore = __decorate([
    linkedShape,
    __metadata("design:paramtypes", [Object])
], InMemoryStore);
export { InMemoryStore };
//# sourceMappingURL=InMemoryStore.js.map